import { Component, Input, computed, signal } from '@angular/core';
import { ImagesWidget, ImageItem, Theme, ImagesLayout, WidgetColors } from '../../models/reader-document';
import { NgTemplateOutlet } from '@angular/common';
import { createCarouselController } from './images-carousel.logic';
import { DEFAULT_COLORS, DEFAULT_PADDING } from '../../utils/widget.constants';
import { resolveWidgetStyleColors } from '../../utils/widget-style.utils';

type DarkTheme = Theme & { isDark?: boolean };

@Component({
  selector: 'wr-images-widget',
  standalone: true,
  imports: [NgTemplateOutlet],
  templateUrl: './images.component.html'
})
export class ImagesWidgetComponent {
  private widgetSig = signal<ImagesWidget | null>(null);
  private themeSig = signal<DarkTheme | null>(null);

  private carousel = createCarouselController<ImageItem>({
    items: () => this.images(),
    isInteractiveTarget: (t) => !!(t as HTMLElement)?.closest?.('button,a'),
    swipeThreshold: 35,
    lockAngleRatio: 1.2,
    transitionMs: 220,
  });

  prevImage = this.carousel.prevItem;
  nextImage = this.carousel.nextItem;

  trackTransform = this.carousel.trackTransform;
  trackTransition = this.carousel.trackTransition;

  onTrackTransitionEnd = (e: TransitionEvent) => this.carousel.onTrackTransitionEnd(e);

  @Input() set json(value: ImagesWidget) {
    this.widgetSig.set(value);
    this.carousel.indexSig.set(0);
  }

  @Input() set theme(value: Theme | undefined) {
    this.themeSig.set((value as DarkTheme) ?? null);
  }

  widget = computed(() => this.widgetSig());
  images = computed(() => this.widget()?.images ?? []);
  activeIndex = this.carousel.activeIndex;
  activeImage = this.carousel.activeItem;

  layout = computed<ImagesLayout | 'horizontal' | undefined>(() => {
    const w = this.widget();
    return (w as any)?.layout ?? undefined;
  });

  isCarousel = computed(() => {
    const w = this.widget();
    if (!w) return false;
    return this.layout() === 'horizontal' || w.type === 'imageSlider' || w.type === 'imagesHorizontal';
  });

  // styling
  mergedColors = computed<WidgetColors>(() => {
    const w = this.widget();
    const theme = this.themeSig();
    const isDark = theme?.isDark ?? false;

    const themeColors: WidgetColors = theme
      ? { ...theme[isDark ? 'darkColors' : 'lightColors'] }
      : {};

    const widgetColors: WidgetColors = resolveWidgetStyleColors(w?.widgetStyle, isDark);

    return {
      accent: widgetColors.accent ?? themeColors.accent ?? DEFAULT_COLORS.accent,
      title: widgetColors.title ?? themeColors.title ?? DEFAULT_COLORS.title,
      subtitle: widgetColors.subtitle ?? themeColors.subtitle ?? DEFAULT_COLORS.subtitle,
      primary: widgetColors.primary ?? themeColors.primary ?? DEFAULT_COLORS.primary,
      background: widgetColors.background ?? themeColors.background ?? DEFAULT_COLORS.background,
    };
  });

  background = computed(() => this.mergedColors()?.background);
  titleColor = computed(() => this.mergedColors()?.title);
  subtitleColor = computed(() => this.mergedColors()?.subtitle);
  cornerRadius = computed(() => this.themeSig()?.cornerRadius ?? 0);
  titleFont = computed(() => this.themeSig()?.fonts?.title?.name ?? this.themeSig()?.fonts?.plain?.name ?? 'inherit');
  subtitleFont = computed(() => this.themeSig()?.fonts?.subtitle?.name ?? this.themeSig()?.fonts?.plain?.name ?? 'inherit');
  paddingTop = computed(() => (this.widget()?.widgetStyle?.padding?.top ?? DEFAULT_PADDING.top) + 'px');
  paddingBottom = computed(() => (this.widget()?.widgetStyle?.padding?.bottom ?? DEFAULT_PADDING.bottom) + 'px');
  dotActive = computed(() =>
    this.mergedColors()?.accent ?? DEFAULT_COLORS.accent
  );
  dotInactive = computed(() =>
    this.themeSig()?.isDark
      ? 'rgba(255,255,255,.35)'
      : 'rgba(0,0,0,.25)'
  );
  textAlign = computed(() => {
    const align = this.widget()?.widgetStyle?.contentAlignment;
    if (align === 'leading') return 'left';
    if (align === 'center') return 'center';
    if (align === 'trailing') return 'right';
    return 'left';
  });

  // ---- helpers for grid sizing
  aspect(fill?: boolean, compact?: boolean, mode?: 'grid' | 'carousel') {

    if (mode === 'carousel') return '1 / 1';
    if (fill) return '1 / 1';
    if (compact) return '16 / 9';
    return '1 / 1';
  }

  imageAspect = (im: ImageItem | null | undefined) => {
    const w = im?.size?.[0];
    const h = im?.size?.[1];
    if (!w || !h || w <= 0 || h <= 0) return '1 / 1';
    return `${w} / ${h}`;
  };


  firstN(n: number): ImageItem[] {
    return (this.images() ?? []).slice(0, n);
  }

  imgSrc(im: ImageItem): string {
    return im.url || im.fallbackUrl || '';
  }

  safeLink(im: ImageItem): string | null {
    const link = im.linkURL?.trim();
    if (!link) return null;
    return link;
  }

  altText(im: ImageItem): string {
    return im.altTitle || im.title || im.id || '';
  }

  onImgError(event: Event, im: ImageItem) {
    const el = event.target as HTMLImageElement;
    if (im.fallbackUrl && el.src !== im.fallbackUrl) {
      el.src = im.fallbackUrl;
      return;
    }
    el.style.display = 'none';
  }

  // CAROUSEL

  onPointerDown = (e: PointerEvent) => this.carousel.onPointerDown(e);
  onPointerMove = (e: PointerEvent) => this.carousel.onPointerMove(e);
  onPointerUp = (e: PointerEvent) => this.carousel.onPointerUp(e);
  prev = () => this.carousel.prev();
  next = () => this.carousel.next();
  goTo = (i: number) => this.carousel.goTo(i);


  measuredAspect = signal<string | null>(null);

  onFirstImageLoad(event: Event) {
    const img = event.target as HTMLImageElement;
    const w = img.naturalWidth;
    const h = img.naturalHeight;
    if (w > 0 && h > 0) {
      this.measuredAspect.set(`${w} / ${h}`);
    }
  }

  // Support size and non-size docs
  
  carouselAspect = computed(() => {
    const first = this.images()?.[0];
    const w = first?.size?.[0];
    const h = first?.size?.[1];
    if (w && h && w > 0 && h > 0) return `${w} / ${h}`; // from doc
    return this.measuredAspect() ?? '16 / 9'; // fallback to measured or default
  });

  /*   carouselAspect = computed(() => {
      const first = this.images()?.[0];
      const fallback = '16 / 9';
      const w = first?.size?.[0];
      const h = first?.size?.[1];
      console.log('carouselAspect', w, h);
      if (!w || !h || w <= 0 || h <= 0) return fallback;
      console.log('carouselAspect', w, h);

      return `${w} / ${h}`;
    });
  */
  // LAYOUT HELPER

  twoHorizontalAspect = computed(() => {
    const imgs = this.firstN(2);
    if (!imgs.length) return '1 / 1';

    // Check if any image is vertical
    for (const im of imgs) {
      const w = im.size?.[0] ?? 0;
      const h = im.size?.[1] ?? 0;
      if (w > 0 && h > 0) {
        if (h > w) {
          return `${w} / ${h}`; // use vertical
        }
      }
    }

    // Fallback to natural aspect of the first image
    const first = imgs[0];
    const w = first.size?.[0] ?? 1;
    const h = first.size?.[1] ?? 1;
    return `${w} / ${h}`;
  });

}
